import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Indicator,
  Menu,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { IconBell } from '@tabler/icons-react';
import { api } from '../api/client';

/**
 * Колокольчик: что случилось, пока человек смотрел в другое место.
 *
 * До него ни одно событие никуда не приходило: полотно сварено в цеху —
 * узнает тот, кто откроет производство; проект утверждён — тот, кто
 * откроет очередь. Заказ неделю ждал, пока о нём вспомнят.
 *
 * ЗДЕСЬ ТОЛЬКО ТО, ПО ЧЕМУ НАДО ДЕЙСТВОВАТЬ. Строка на каждую смену
 * статуса превратила бы список в шум за один день, а шум перестают
 * читать — и настоящее проходит незамеченным. Та же причина, по которой
 * рабочий стол не показывает пустых карточек (правило 48).
 */

interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  entityType: string | null;
  entityId: string | null;
  readAt: string | null;
  createdAt: string;
}

/** Куда вести по нажатию. Неизвестный тип никуда не ведёт. */
function routeFor(n: Notification): string | null {
  switch (n.entityType) {
    case 'deal':
      return n.entityId ? `/deals/${n.entityId}` : null;
    case 'installation':
      return '/installations';
    case 'production':
      return '/production';
    case 'project':
      return '/design';
    // Обращения разбирают на вкладке гарантии — там же, куда ведёт
    // карточка рабочего стола
    case 'warranty':
      return '/installations?tab=warranty';
    // Расходку выдают в самой карточке: список заставил бы искать в нём
    // ту, о которой сказали (правило 121)
    case 'consumables':
      return n.entityId ? `/consumables/${n.entityId}` : '/consumables';
    default:
      return null;
  }
}

/** «5 минут назад» — точное время здесь не нужно, нужна свежесть. */
function ago(iso: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;
  return new Date(iso).toLocaleDateString('ru-RU');
}

export function NotificationBell() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: Notification[]; unread: number }>('/notifications'),
    // Минута — не «в реальном времени», но и не «когда откроет».
    // Чаще незачем: события здесь измеряются часами работы цеха
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;

  return (
    <Menu shadow="md" width={340} position="bottom-end">
      <Menu.Target>
        <Indicator
          disabled={unread === 0}
          label={unread > 9 ? '9+' : unread}
          size={16}
          color="red"
          offset={4}
        >
          <ActionIcon variant="subtle" size="lg" aria-label="Уведомления">
            <IconBell size={20} />
          </ActionIcon>
        </Indicator>
      </Menu.Target>

      <Menu.Dropdown>
        <Group justify="space-between" px="xs" py={6}>
          <Text size="sm" fw={600}>
            Уведомления
          </Text>
          {unread > 0 && (
            <Button
              size="compact-xs"
              variant="subtle"
              loading={markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              Прочитать все
            </Button>
          )}
        </Group>

        {items.length === 0 ? (
          <Text size="sm" c="dimmed" px="xs" py="sm">
            {/* Перечень обязан совпадать с тем, что и правда шлётся
                (правило 68): «монтаж без денег» долго стоял в перечне и
                не отправлялся, а теперь наоборот — отправляется, а в
                списке его не было */}
            Пока ничего. Здесь появится то, по чему надо действовать: утверждённый
            проект, готовое полотно, сданная работа без денег, низкая оценка.
          </Text>
        ) : (
          <ScrollArea.Autosize mah={360}>
            <Stack gap={2} px={4} pb={4}>
              {items.map((n) => {
                const route = routeFor(n);
                return (
                  <Menu.Item
                    key={n.id}
                    onClick={() => {
                      if (!n.readAt) markRead.mutate(n.id);
                      if (route) navigate(route);
                    }}
                    style={{
                      background: n.readAt ? undefined : 'var(--mantine-color-blue-0)',
                      borderRadius: 4,
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap" gap={4}>
                      <Text size="sm" fw={n.readAt ? 400 : 600} style={{ minWidth: 0 }}>
                        {n.title}
                      </Text>
                      {!n.readAt && (
                        <Badge size="xs" variant="filled" color="blue" circle>
                          {' '}
                        </Badge>
                      )}
                    </Group>
                    {n.body && (
                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'normal' }}>
                        {n.body}
                      </Text>
                    )}
                    <Text size="xs" c="dimmed">
                      {ago(n.createdAt)}
                    </Text>
                  </Menu.Item>
                );
              })}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
